import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

export enum NotificationType {
  ERROR = 'danger',
  INFO = 'info',
  SUCCESS = 'success',
  WARNING = 'warning',
}

interface OneNotification {
  id: number;
  type: NotificationType;
  content: string;
  values?: string | Record<string, ReactNode>;
}

interface NotificationProps {
  notification: OneNotification;
}

interface NotificationsContainerProps {
  notifications: OneNotification[];
}

const Notification: React.FC<NotificationProps> = ({ notification }) => {
  const { removeNotification } = useNotification();
  const { values, type } = notification;

  useEffect(() => {
    const duration = type === NotificationType.ERROR || type === NotificationType.WARNING ? 10000 : 3000;
    const timer = setTimeout(() => removeNotification(notification.id), duration);
    return () => clearTimeout(timer);
  }, [notification, removeNotification, type]);

  return (
    <div
      className={`notification notification-${type} fade show mt-3`}
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      <div className="notification-header">
        <span className="fw-medium me-auto">
          <FormattedMessage id={`notification.${type}`} />
        </span>
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          onClick={() => removeNotification(notification.id)}
        />
      </div>
      <div className="notification-body" style={{ whiteSpace: 'pre-line' }}>
        {values && typeof values === 'object' ? (
          <FormattedMessage id={notification.content} values={values} />
        ) : (
          <>
            <FormattedMessage id={notification.content} />
            {values && <div className="mt-1">{values}</div>}
          </>
        )}
      </div>
    </div>
  );
};

const NotificationsContainer: React.FC<NotificationsContainerProps> = ({ notifications }): JSX.Element => {
  return (
    <div className="notification-banner">
      {notifications.map((notification) => (
        <Notification key={notification.id} notification={notification} />
      ))}
    </div>
  );
};

interface NotificationContextProps {
  notify: (type: NotificationType, content: string, values?: string | Record<string, ReactNode>) => number;
  notifyError: (content: string, error?: Error) => number;
  notifyWarning: (content: string, error?: Error) => number;
  notifySuccess: (content: string) => number;
  removeNotification: (id: number) => void;
}

export const NotificationContext = createContext<NotificationContextProps>({
  notify: (_type: NotificationType, _content: string) => 0,
  notifyError: (_content: string) => 0,
  notifyWarning: (_content: string) => 0,
  notifySuccess: (_content: string) => 0,
  removeNotification: (_id: number) => null,
});

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [notifications, setNotifications] = useState<OneNotification[]>([]);

  const notify = useCallback(
    (type: NotificationType, content: string, values?: string | Record<string, ReactNode>) => {
      const id = Date.now();
      setNotifications((notifications) => [
        ...notifications,
        {
          id,
          type,
          content,
          values,
        },
      ]);
      return id;
    },
    [setNotifications]
  );

  const notifyError = (content: string, error?: Error) => {
    if (error) {
      console.error(error);
    }
    return notify(NotificationType.ERROR, content, error?.message);
  };

  const notifyWarning = (content: string, error?: Error) => {
    if (error) {
      console.warn(error);
    }
    return notify(NotificationType.WARNING, content, error?.message);
  };

  const notifySuccess = (content: string) => notify(NotificationType.SUCCESS, content);

  const removeNotification = useCallback(
    (id: number) => {
      setNotifications((notifications) => notifications.filter((t) => t.id !== id));
    },
    [setNotifications]
  );

  return (
    <NotificationContext.Provider value={{ notify, notifyError, notifyWarning, notifySuccess, removeNotification }}>
      <NotificationsContainer notifications={notifications} />
      {children}
    </NotificationContext.Provider>
  );
};

export function useNotification() {
  return useContext(NotificationContext);
}
